import React from 'react';
import Navigation from '../../components/Navigation';
import Footer from '../../components/Footer';
import { Settings, Wrench, Truck, HeadphonesIcon } from 'lucide-react';

const Services = () => {
  const services = [
    {
      icon: Settings,
      title: "Custom Door Design",
      description: "Tailored door solutions to meet your specific requirements and architectural needs.",
      features: ["Custom dimensions", "Material selection", "Hardware options", "Color matching"]
    },
    {
      icon: Wrench,
      title: "Professional Installation",
      description: "Expert installation services ensuring perfect fit and optimal performance.",
      features: ["Certified installers", "Quality guarantee", "Proper sealing", "Hardware setup"]
    },
    {
      icon: Truck,
      title: "Delivery & Logistics",
      description: "Reliable delivery services across Estonia and neighboring regions.",
      features: ["Secure packaging", "Timely delivery", "Installation scheduling", "Damage protection"]
    },
    {
      icon: HeadphonesIcon,
      title: "Technical Support",
      description: "Comprehensive support from consultation to after-sales service.",
      features: ["Expert consultation", "Technical guidance", "Maintenance tips", "Warranty support"]
    }
  ];

  const pageStyle = {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #000000 0%, #1a1a1a 50%, #2d2d2d 100%)',
    paddingTop: '4rem'
  };

  const containerStyle = {
    maxWidth: '72rem',
    margin: '0 auto',
    padding: '2rem 1.5rem'
  };

  const headerStyle = {
    textAlign: 'center' as const,
    marginBottom: '4rem'
  };

  const titleStyle = {
    fontSize: '3rem',
    fontWeight: 'bold',
    color: 'white',
    marginBottom: '1rem'
  };

  const highlightStyle = {
    color: '#dfa926'
  };

  const descriptionStyle = {
    fontSize: '1.25rem',
    color: '#d1d5db',
    maxWidth: '48rem',
    margin: '0 auto',
    lineHeight: '1.6'
  };

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '2rem',
    marginBottom: '4rem'
  };

  const cardStyle = {
    background: 'linear-gradient(135deg, rgba(26, 26, 26, 0.5) 0%, rgba(17, 17, 17, 0.5) 100%)',
    padding: '2rem',
    borderRadius: '0.75rem',
    backdropFilter: 'blur(8px)',
    border: '1px solid rgba(58, 58, 58, 0.5)',
    transition: 'all 0.3s ease'
  };

  const iconStyle = {
    width: '3rem',
    height: '3rem',
    color: '#dfa926',
    marginBottom: '1.5rem'
  };

  const cardTitleStyle = {
    fontSize: '1.5rem',
    fontWeight: '600',
    color: 'white',
    marginBottom: '1rem'
  };

  const cardDescStyle = {
    color: '#9ca3af',
    marginBottom: '1.5rem',
    lineHeight: '1.6'
  };

  const featureListStyle = {
    listStyle: 'none',
    padding: 0,
    margin: 0
  };

  const featureItemStyle = {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '0.5rem',
    color: '#d1d5db',
    fontSize: '0.875rem'
  };

  const bulletStyle = {
    width: '0.5rem',
    height: '0.5rem',
    backgroundColor: '#dfa926',
    borderRadius: '50%',
    marginRight: '0.75rem'
  };

  return (
    <div style={pageStyle}>
      <Navigation />
      
      <div style={containerStyle}>
        <div style={headerStyle}>
          <h1 style={titleStyle}>
            Our <span style={highlightStyle}>Services</span>
          </h1>
          <p style={descriptionStyle}>
            From initial consultation to final installation, we provide comprehensive 
            services to ensure your door project exceeds expectations.
          </p>
        </div>

        <div style={gridStyle}>
          {services.map((service, index) => {
            const IconComponent = service.icon;
            return (
              <div 
                key={index}
                style={cardStyle}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(223, 169, 38, 0.5)';
                  e.currentTarget.style.transform = 'translateY(-4px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(58, 58, 58, 0.5)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <IconComponent style={iconStyle} />
                <h3 style={cardTitleStyle}>{service.title}</h3>
                <p style={cardDescStyle}>{service.description}</p>
                
                <ul style={featureListStyle}>
                  {service.features.map((feature, featureIndex) => (
                    <li key={featureIndex} style={featureItemStyle}>
                      <span style={bulletStyle}></span>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        <div style={{ textAlign: 'center', marginTop: '3rem' }}>
          <div style={{
            background: 'linear-gradient(135deg, rgba(223, 169, 38, 0.1) 0%, rgba(223, 169, 38, 0.05) 100%)',
            padding: '3rem',
            borderRadius: '0.75rem',
            border: '1px solid rgba(223, 169, 38, 0.2)'
          }}>
            <h3 style={{ color: 'white', fontSize: '2rem', marginBottom: '1rem' }}>
              Ready to Get Started?
            </h3>
            <p style={{ color: '#d1d5db', marginBottom: '2rem', fontSize: '1.125rem' }}>
              Contact us today to discuss your door project and receive a personalized quote.
            </p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <a 
                href="/contact" 
                style={{
                  backgroundColor: '#dfa926',
                  color: 'black',
                  padding: '0.75rem 2rem',
                  borderRadius: '0.5rem',
                  textDecoration: 'none',
                  fontWeight: '600',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => (e.target as HTMLElement).style.backgroundColor = '#c8941f'}
                onMouseLeave={(e) => (e.target as HTMLElement).style.backgroundColor = '#dfa926'}
              >
                Get Quote
              </a>
              <a 
                href="/products" 
                style={{
                  border: '2px solid #dfa926',
                  color: '#dfa926',
                  backgroundColor: 'transparent',
                  padding: '0.75rem 2rem',
                  borderRadius: '0.5rem',
                  textDecoration: 'none',
                  fontWeight: '600',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  (e.target as HTMLElement).style.backgroundColor = '#dfa926';
                  (e.target as HTMLElement).style.color = 'black';
                }}
                onMouseLeave={(e) => {
                  (e.target as HTMLElement).style.backgroundColor = 'transparent';
                  (e.target as HTMLElement).style.color = '#dfa926';
                }}
              >
                View Products
              </a>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Services;
